import React, { useState, useCallback, useRef, useEffect } from 'react';
import { X, Upload, Search, Image as ImageIcon, Film, Trash2, Check, Loader2, Grid, List } from 'lucide-react';
import { useMediaList, useMediaUpload, useMediaDelete } from '../../../hooks/useMedia';

export default function MediaLibraryModal({ isOpen, onClose, onSelect, acceptTypes = 'all', currentUrl = '' }) {
  // acceptTypes: 'all', 'image', 'video'
  // currentUrl: URL of currently selected media (to pre-select in the library)

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState(acceptTypes === 'all' ? 'all' : acceptTypes);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const fileInputRef = useRef(null);

  const { media, loading, error, refetch } = useMediaList({
    type: filterType,
    search: searchTerm,
    sort: sortBy,
    order: sortOrder,
  });

  const { uploadMedia, uploading, progress } = useMediaUpload();
  const { deleteMedia, deleting } = useMediaDelete();

  // Pre-select media item that matches the currentUrl when modal opens
  useEffect(() => {
    if (isOpen && currentUrl && media.length > 0 && !selectedMedia) {
      const matchingMedia = media.find(item => item.public_url === currentUrl);
      if (matchingMedia) {
        setSelectedMedia(matchingMedia);
      }
    }
  }, [isOpen, currentUrl, media, selectedMedia]);

  // Reset selection when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedMedia(null);
    }
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleFileSelect = useCallback(async (files) => {
    const fileArray = Array.from(files);

    for (const file of fileArray) {
      // Validate file type
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');

      if (!isImage && !isVideo) {
        continue;
      }

      if (acceptTypes === 'image' && !isImage) continue;
      if (acceptTypes === 'video' && !isVideo) continue;

      try {
        await uploadMedia(file, () => {
          refetch();
        });
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
  }, [uploadMedia, refetch, acceptTypes]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDelete = async (mediaItem, e) => {
    e.stopPropagation();
    if (window.confirm(`Delete "${mediaItem.filename}"? This cannot be undone.`)) {
      await deleteMedia(mediaItem.id, () => {
        if (selectedMedia?.id === mediaItem.id) {
          setSelectedMedia(null);
        }
        refetch();
      });
    }
  };

  const handleSelect = () => {
    if (selectedMedia) {
      onSelect(selectedMedia);
      onClose();
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900">Media Library</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-200 rounded-md transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-4 px-6 py-3 border-b">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search media..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Filter */}
          {acceptTypes === 'all' && (
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="image">Images</option>
              <option value="video">Videos</option>
            </select>
          )}

          {/* Sort */}
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field);
              setSortOrder(order);
            }}
            className="text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="created_at-desc">Newest First</option>
            <option value="created_at-asc">Oldest First</option>
            <option value="filename-asc">Name A-Z</option>
            <option value="filename-desc">Name Z-A</option>
            <option value="file_size-desc">Largest First</option>
            <option value="file_size-asc">Smallest First</option>
          </select>

          {/* View Toggle */}
          <div className="flex border rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>

          {/* Upload Button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptTypes === 'image' ? 'image/*' : acceptTypes === 'video' ? 'video/*' : 'image/*,video/*'}
            multiple
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>

        {/* Upload Progress */}
        {uploading && (
          <div className="px-6 py-2 bg-blue-50 border-b">
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-sm text-blue-700">{progress}%</span>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div
          className={`flex-1 overflow-auto p-6 ${isDragging ? 'bg-blue-50' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full text-red-500">
              {error}
            </div>
          ) : media.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <Upload className="w-12 h-12 mb-4 text-gray-300" />
              <p className="text-lg font-medium">No media files yet</p>
              <p className="text-sm">Drag and drop files here or click Upload</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {media.map((item) => (
                <div
                  key={item.id}
                  onClick={() => setSelectedMedia(item)}
                  className={`group relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                    selectedMedia?.id === item.id
                      ? 'border-blue-500 ring-2 ring-blue-200'
                      : 'border-transparent hover:border-gray-300'
                  }`}
                >
                  {item.file_type.startsWith('image/') ? (
                    <img
                      src={item.public_url}
                      alt={item.filename}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-200">
                      <Film className="w-12 h-12 text-gray-400" />
                    </div>
                  )}

                  {/* Selection indicator */}
                  {selectedMedia?.id === item.id && (
                    <div className="absolute top-2 left-8 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}

                  {/* Delete button - top right corner on hover */}
                  <button
                    onClick={(e) => handleDelete(item, e)}
                    disabled={deleting}
                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>

                  {/* Filename */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                    <p className="text-xs text-white truncate">{item.filename}</p>
                  </div>

                  {/* Type badge */}
                  <div className="absolute top-2 left-2">
                    {item.file_type.startsWith('image/') ? (
                      <ImageIcon className="w-4 h-4 text-white drop-shadow" />
                    ) : (
                      <Film className="w-4 h-4 text-white drop-shadow" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {media.map((item) => (
                <div
                  key={item.id}
                  onClick={() => setSelectedMedia(item)}
                  className={`flex items-center gap-4 p-3 rounded-lg cursor-pointer border transition-all ${
                    selectedMedia?.id === item.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {/* Thumbnail */}
                  <div className="w-16 h-16 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
                    {item.file_type.startsWith('image/') ? (
                      <img
                        src={item.public_url}
                        alt={item.filename}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Film className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{item.filename}</p>
                    <p className="text-sm text-gray-500">
                      {formatFileSize(item.file_size)} • {item.width && item.height ? `${item.width}×${item.height}` : item.file_type}
                    </p>
                  </div>

                  {/* Date */}
                  <div className="text-sm text-gray-500">
                    {formatDate(item.created_at)}
                  </div>

                  {/* Delete */}
                  <button
                    onClick={(e) => handleDelete(item, e)}
                    disabled={deleting}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>

                  {/* Selection */}
                  {selectedMedia?.id === item.id && (
                    <Check className="w-5 h-5 text-blue-500" />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Drag overlay */}
          {isDragging && (
            <div className="absolute inset-0 flex items-center justify-center bg-blue-50 bg-opacity-90 border-2 border-dashed border-blue-300 rounded-lg m-4">
              <div className="text-center">
                <Upload className="w-12 h-12 mx-auto mb-2 text-blue-500" />
                <p className="text-lg font-medium text-blue-700">Drop files to upload</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
          <div className="text-sm text-gray-500">
            {selectedMedia ? (
              <>
                Selected: <span className="font-medium text-gray-900">{selectedMedia.filename}</span>
                {selectedMedia.width && selectedMedia.height && (
                  <span className="ml-2">({selectedMedia.width}×{selectedMedia.height})</span>
                )}
              </>
            ) : (
              'Select a file from the library'
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedMedia}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Select
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
